import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Star,
  ChevronLeft,
  ChevronRight,
  Share2,
  Download,
  Mail,
  Info,
  MapPin,
  CheckCircle2,
  Hotel,
  Utensils,
  Plane,
  Camera,
  Bus,
  UserRound,
  Phone,
  CalendarDays,
  ChevronDown,
  BadgeIndianRupee,
  StickyNote as StampIcon,
  Sparkles,
  FileText,
  ShieldCheck,
  IndianRupee,
  PlaneTakeoff,
  Building2,
} from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/sections/Navbar";
import { Footer } from "@/components/sections/Footer";
import { FloatingWidgets } from "@/components/sections/FloatingWhatsApp";
import { SmartImage } from "@/components/SmartImage";
import { DatesDialog } from "@/components/india/TourCard";
import { EnquiryDrawer } from "@/components/india/EnquiryForm";
import { getTourBySlug, formatINR, TOURS } from "@/data/tours";
import { INDIA_FAQS } from "@/data/indiaContent";
import { BRAND } from "@/data/content";
import { postLead } from "@/lib/leads";
import { resolveAssetPath } from "@/lib/assets";
import { downloadItineraryPdf } from "@/lib/itineraryPdf";
import { DeparturePicker } from "@/components/india/DeparturePicker";
import { EmailItineraryDialog } from "@/components/india/EmailItineraryDialog";
import { MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const buildItinerary = (tour) => {
  const days = [];
  let dayNum = 1;
  tour.route.forEach((r) => {
    r.stops.forEach((stop) => {
      const nights = (stop.match(/\((\d+)N\)/) || [])[1];
      const place = stop.replace(/\s*\(\d+N\)/, "");
      const count = nights ? Number(nights) : 1;
      days.push({
        day: dayNum,
        title: dayNum === 1 ? `Arrive in ${place}` : `Explore ${place}`,
        state: r.state,
        desc:
          dayNum === 1
            ? `Arrive and check in. Meet your Royal Tours manager and fellow travellers, followed by a welcome briefing and an evening at leisure in ${place}.`
            : `A full day of curated sightseeing across ${place} covering iconic landmarks, local experiences and time to soak in the culture.`,
        nights,
      });
      for (let k = 1; k < count; k++) {
        days.push({
          day: dayNum + k,
          title: `More of ${place}`,
          state: r.state,
          desc: `Continue exploring ${place} with more sightseeing, local markets and free time to enjoy the destination at your own pace.`,
        });
      }
      dayNum += count;
    });
  });
  return days.slice(0, tour.days);
};

const InclusionIcon = ({ Icon, label, on, optional }) => (
  <div className={`flex flex-col items-center gap-2 ${on ? "" : "opacity-30"}`}>
    <div className="relative w-14 h-14 rounded-full bg-[var(--soft)] ring-1 ring-gray-200 flex items-center justify-center">
      {optional && <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-[var(--yellow)] text-[var(--navy)] text-[7px] font-bold px-1 rounded">Optional</span>}
      <Icon className="w-6 h-6 text-[var(--yellow-2)]" strokeWidth={1.6} />
    </div>
    <span className="text-[12px] text-[#0b2545]/80">{label}</span>
  </div>
);

export default function TourDetail() {
  const { slug } = useParams();
  const tour = getTourBySlug(slug) || TOURS[0];
  const [datesOpen, setDatesOpen] = useState(false);
  const [enquiry, setEnquiry] = useState(false);
  const [galIdx, setGalIdx] = useState(0);
  const [emiForm, setEmiForm] = useState({ name: "", mobile: "" });

  const itinerary = useMemo(() => tour.itinerary?.length ? tour.itinerary : buildItinerary(tour), [tour]);
  const gallery = tour.gallery?.length ? tour.gallery : [tour.image];

  const emiRows = tour.startingPrice == null ? [] : [6, 9, 12].map((m) => ({
    months: m,
    monthly: Math.round((tour.startingPrice * 1.0125) / m / 10) * 10,
    interest: "1.25%",
  }));

  const depCity = tour.departureCity || "Mumbai";
  const experience = tour.experience?.length ? tour.experience : ["Culture", "Heritage", "Sightseeing"];

  const cancelTiers = tour.startingPrice == null ? [] : [
    { pct: 10, window: "121–900 days prior" },
    { pct: 15, window: "91–120 days prior" },
    { pct: 20, window: "61–90 days prior" },
    { pct: 30, window: "46–60 days prior" },
    { pct: 40, window: "31–45 days prior" },
    { pct: 50, window: "16–30 days prior" },
    { pct: 75, window: "6–15 days prior" },
    { pct: 100, window: "0–5 days prior" },
  ].map((t) => ({ ...t, amount: Math.round((tour.startingPrice * t.pct) / 100) }));

  const INCLUSIONS_TEXT = tour.information?.inclusions || [
    `To and fro economy class air ticket (Ex-${depCity}) with current airport taxes`,
    "Baggage allowance as per airline policy",
    "Royal Tours Tour Manager services from Day 1 meeting point to the last-day dropping point",
    "Travel by comfortable A/C vehicles (car / tempo traveller / bus) depending on group size",
    "Accommodation in comfortable hotels on twin / triple / single sharing basis",
    "All meals — breakfast, lunch & dinner (set menu) as mentioned in the itinerary",
    "Guide & driver tips, hotel & restaurant tips",
    "Entrance fees of all sightseeing places visited from inside",
    "Accompanied local guide services wherever required",
    "1 litre water bottle per person per day",
    "Travel insurance for guests up to 70 years of age",
  ];
  const EXCLUSIONS_TEXT = tour.information?.exclusions || [
    "Any airfare or taxes other than those mentioned in inclusions",
    "Cost of pre / post tour hotel stay",
    "Personal expenses — laundry, telephone, room service, tips, etc.",
    "5% GST applicable on the tour price",
    "Anything not specifically mentioned under inclusions",
  ];
  const PREP_TEXT = tour.information?.preparation || [
    "Carry comfortable clothing and good walking shoes",
    "Keep original photo IDs and photocopies handy",
    "Carry personal medication along with prescriptions",
    "Carry two passport-size photographs",
  ];
  const DOCS = tour.travelInfo?.documents || [
    "Adult: Voter ID / Passport / Aadhaar / Driving Licence",
    "Child: Passport / Aadhaar / School ID",
    "Infant: Aadhaar / Birth certificate",
  ];
  const flightRoute = tour.travelInfo?.airRoute || `${depCity} — ${tour.route[0]?.state} // ${tour.route[tour.route.length - 1]?.state} — ${depCity}`;


  const inclusions = [
    { Icon: Hotel, label: "Hotel", on: tour.inclusions.hotel },
    { Icon: Utensils, label: "Meals", on: tour.inclusions.meals },
    { Icon: Plane, label: "Flight", on: !!tour.inclusions.flight, optional: tour.inclusions.flight === "optional" },
    { Icon: Camera, label: "Sightseeing", on: tour.inclusions.sightseeing },
    { Icon: Bus, label: "Transport", on: tour.inclusions.transport },
    ...(tour.inclusions.visa !== undefined ? [{ Icon: StampIcon, label: "Visa", on: tour.inclusions.visa }] : []),
  ];

  const [pdfBusy, setPdfBusy] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [departure, setDeparture] = useState(null);
  const shownPrice = departure?.price ?? tour.startingPrice;
  const pdfOptions = { tour, departure, depCity, itinerary, inclusionsText: INCLUSIONS_TEXT, exclusionsText: EXCLUSIONS_TEXT, prepText: PREP_TEXT, docs: DOCS, cancelTiers, flightRoute };
  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      await downloadItineraryPdf(pdfOptions);
      toast.success("Itinerary PDF downloaded");
    } catch {
      toast.error("Couldn't create the PDF. Please try again.");
    } finally {
      setPdfBusy(false);
    }
  };
  const shareItinerary = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: tour.title, text: `${tour.title} - ${tour.days} days with ${BRAND.shortName}`, url }); } catch { /* cancelled */ }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied - share it with anyone");
    } catch {
      toast.error(`Couldn't copy automatically. Share this link: ${url}`);
    }
  };
  const shareWhatsApp = () => {
    const route = tour.route.map((x) => x.stops.map((st) => st.replace(/\s*\(.*\)/, "")).join(", ")).join(" | ");
    const lines = [
      `*${tour.title}* (${tour.code || "Tour"})`,
      `${tour.days} Days / ${tour.nights ?? tour.days - 1} Nights`,
      `Route: ${route}`,
      departure ? `Departure: ${departure.date}` : tour.dates?.[0]?.date ? `Next departure: ${tour.dates[0].date}` : null,
      shownPrice == null ? "Price: on request" : `Price: ${formatINR(shownPrice)} per person${departure ? "" : " onwards"}`,
      tour.highlights?.length ? `Highlights: ${tour.highlights.slice(0, 4).join(", ")}` : null,
      "",
      `View details: ${window.location.origin}/tour/${tour.slug}`,
      `- ${BRAND.shortName}, ${BRAND.phone}`,
    ].filter((l) => l !== null);
    window.open(`https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`, "_blank", "noopener");
  };
  const ACTIONS = [
    { Icon: Share2, label: "Share Itinerary", testid: "action-share", onClick: shareItinerary },
    { Icon: Download, label: pdfBusy ? "Preparing PDF..." : "Download Itinerary", testid: "action-download", onClick: downloadPdf },
    { Icon: MessageCircle, label: "WhatsApp", testid: "action-whatsapp", onClick: shareWhatsApp },
    { Icon: Mail, label: "Email Itinerary", testid: "action-email", onClick: () => setEmailOpen(true) },
  ];

  const submitEmi = async () => {
    if (!emiForm.name.trim() || emiForm.mobile.length < 10) {
      toast.error("Enter your name and a valid 10-digit mobile number.");
      return;
    }
    try {
      await postLead({
        name: emiForm.name,
        phone: emiForm.mobile,
        destination: tour.title,
        message: "EMI eligibility enquiry",
        source: "emi",
      });
      toast.success("Details submitted! We'll check your EMI eligibility and get back.");
      setEmiForm({ name: "", mobile: "" });
    } catch (e) {
      toast.error("Something went wrong. Please try again or call us directly.");
    }
  };

  return (
    <div className="bg-white text-[var(--navy)] overflow-x-hidden">
      <Header />

      {/* Breadcrumb */}
      <div className="max-w-[1280px] mx-auto px-4 lg:px-6 pt-6">
        <nav className="text-[12px] text-gray-500" data-testid="breadcrumb">
          <Link to="/" className="hover:text-[var(--navy)]">Home</Link>
          <span className="mx-1.5">&gt;</span>
          <Link to={tour.scope === "world" ? "/world" : "/india"} className="hover:text-[var(--navy)]">{tour.scope === "world" ? "World" : "India"}</Link>
          <span className="mx-1.5">&gt;</span>
          <span>{tour.route[0]?.state}</span>
          <span className="mx-1.5">&gt;</span>
          <span className="text-[var(--navy)] font-medium">{tour.title}</span>
        </nav>
      </div>

      {/* Hero */}
      <section className="max-w-[1280px] mx-auto px-4 lg:px-6 pt-5">
        <div className="grid lg:grid-cols-[1fr_360px] gap-6 items-start">
          {/* Left: image + review overlay */}
          <div>
            <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="relative rounded-xl overflow-hidden h-[300px] md:h-[360px]" data-testid="tour-hero">
              <SmartImage src={gallery[galIdx]} alt={tour.title} className="w-full h-full object-cover" priority />
              <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent" />
              {/* Review card */}
              {tour.review && <div className="absolute top-4 right-4 w-[300px] max-w-[70%] bg-white/95 backdrop-blur rounded-lg p-4 shadow-lg hidden sm:block">
                <div className="flex items-center justify-between mb-2">
                  <span className="inline-flex items-center gap-1 font-bold text-[var(--navy)]"><Star className="w-4 h-4 fill-[var(--yellow-2)] text-[var(--yellow-2)]" /> {tour.rating}</span>
                  <span className="text-[13px] text-gray-600">{tour.guestsRated}+ Guests already travelled</span>
                </div>
                <p className="text-[12.5px] text-[#0b2545]/75 leading-snug border-t border-gray-100 pt-2">"{tour.review.text}"</p>
                <div className="flex items-center justify-between mt-3 text-[11px]">
                  <div>
                    <div className="font-semibold text-[var(--navy)]">{tour.review.author}</div>
                    <div className="text-gray-400">{tour.review.date}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-[var(--navy)]">{tour.review.manager}</div>
                    <div className="text-gray-400">Tour Manager</div>
                  </div>
                </div>
              </div>}
              {/* arrows */}
              <button type="button" onClick={() => setGalIdx((i) => (i - 1 + gallery.length) % gallery.length)} className="absolute left-3 bottom-3 w-9 h-9 rounded-full bg-white/80 hover:bg-white flex items-center justify-center text-[var(--navy)]" aria-label="prev"><ChevronLeft className="w-4 h-4" /></button>
              <button type="button" onClick={() => setGalIdx((i) => (i + 1) % gallery.length)} className="absolute left-14 bottom-3 w-9 h-9 rounded-full bg-white/80 hover:bg-white flex items-center justify-center text-[var(--navy)]" aria-label="next"><ChevronRight className="w-4 h-4" /></button>
            </motion.div>

            {/* Title block */}
            <div className="mt-5">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider">
                  <span className="bg-[#fcd34d]/15 text-[var(--yellow-2)] border border-[#f59e0b]/40 px-1.5 py-0.5 rounded-l">{tour.tourType}</span>
                  <span className="bg-[var(--navy)] text-white px-1.5 py-0.5 rounded-r -ml-1">{tour.code}</span>
                </span>
                {tour.speciality && tour.speciality.toLowerCase() !== (tour.tourType || "").toLowerCase() && <span className="text-[10px] font-semibold text-[var(--red)] border border-[#e11d2e]/30 rounded px-1.5 py-0.5 uppercase">{tour.speciality}</span>}
              </div>
              <h1 className="font-serif-display text-3xl md:text-[40px] text-[var(--navy)] leading-tight">{tour.title}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-3 text-[13px]">
                <span className="border border-gray-200 rounded-full px-3 py-1 font-medium">{tour.days} Days</span>
                <span className="border border-gray-200 rounded-full px-3 py-1 font-medium inline-flex items-center gap-1">
                  {tour.countriesCount ? <>{tour.countriesCount} {tour.countriesCount > 1 ? "Countries" : "Country"} <span className="w-1 h-1 rounded-full bg-gray-300" /> </> : <>{tour.statesCount} States <span className="w-1 h-1 rounded-full bg-gray-300" /> </>}
                  {tour.citiesCount} Cities <Info className="w-3 h-3 text-gray-400" />
                </span>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="experience-chips">
                <span className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">Experience</span>
                {experience.map((e) => (
                  <span key={e} className="inline-flex items-center gap-1 text-[12px] font-medium text-[var(--navy)] bg-[#fcd34d]/15 border border-[#f59e0b]/30 rounded-full px-2.5 py-1">
                    <Sparkles className="w-3 h-3 text-[var(--yellow-2)]" /> {e}
                  </span>
                ))}
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-[#0b2545]/80">
                {tour.route.map((r, i) => (
                  <span key={r.state} className="inline-flex items-center gap-2">
                    <MapPin className="w-3.5 h-3.5 text-[var(--yellow-2)]" />
                    <b className="text-[var(--navy)]">{r.state}</b>
                    <span className="text-gray-500">{r.stops.join(" ⟶ ")}</span>
                    {i < tour.route.length - 1 && <span className="text-gray-300">⟶</span>}
                  </span>
                ))}
              </div>
              <a href="#itinerary" className="mt-4 inline-flex items-center gap-1 font-semibold text-[var(--navy)] underline underline-offset-4 decoration-[var(--yellow-2)] decoration-2" data-testid="view-itinerary-link">
                View day-wise tour itinerary <ChevronRight className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Right: gallery + price card */}
          <div className="lg:sticky lg:top-24 space-y-4">
            <div className="grid grid-cols-3 gap-2">
              {gallery.slice(0, 3).map((g, i) => (
                <button key={i} type="button" onClick={() => setGalIdx(i)} className="relative rounded-lg overflow-hidden h-20 group">
                  <SmartImage src={g} alt="" fallbackIndex={i} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  {i === 2 && tour.guestPhotos > 0 && (
                    <span className="absolute inset-0 bg-black/55 flex items-center justify-center text-white text-[12px] font-semibold text-center leading-tight">+{tour.guestPhotos} guest photos</span>
                  )}
                </button>
              ))}
            </div>
            {tour.review && <div className="flex items-center gap-2 text-[13px]">
              <span className="flex text-[var(--yellow-2)]">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className="w-4 h-4 fill-[var(--yellow-2)]" />)}</span>
              <span className="font-semibold text-[var(--navy)]">{tour.guestsRated} + guests rated 5*</span>
              <span className="w-px h-4 bg-gray-300" />
              <button type="button" className="font-semibold text-[var(--navy)] underline">{tour.reviewsCount} Reviews</button>
            </div>}

            <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-[0_20px_45px_-25px_rgba(11,37,69,0.5)]" data-testid="price-card">
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <div className="text-[12px] text-gray-500">{departure ? "Price for your date" : "Starting price per person"}</div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[26px] font-bold text-[var(--navy)]" data-testid="price-card-amount">{formatINR(shownPrice)}</span>
                    <Info className="w-3.5 h-3.5 text-gray-400" />
                  </div>
                </div>
                {tour.joiningLeavingPrice && (
                  <div className="border-l border-gray-100 pl-3">
                    <div className="text-[12px] text-gray-500">Joining/Leaving starts from</div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[26px] font-bold text-green-700">{formatINR(tour.joiningLeavingPrice)}</span>
                      <Info className="w-3.5 h-3.5 text-gray-400" />
                    </div>
                  </div>
                )}
              </div>
              <div className="mb-3"><DeparturePicker tour={tour} value={departure} onChange={setDeparture} /></div>
              <Button onClick={() => setDatesOpen(true)} data-testid="dates-availability-btn" className="w-full bg-[var(--yellow)] hover:bg-[var(--yellow-2)] text-[var(--navy)] hover:text-white font-bold py-6 text-[15px] rounded-lg">
                Dates &amp; Availability
              </Button>
              <div className="flex items-center justify-between mt-4 text-[13px]">
                <span className="text-gray-600">EMI starts at <b className="text-[var(--navy)] underline">{tour.emiPerMonth == null ? "Price on request" : formatINR(tour.emiPerMonth) + "/mo"}</b></span>
                <a href="#emi" className="font-semibold text-[var(--navy)] underline">Pricing details</a>
              </div>
              <div className="grid grid-cols-4 gap-2 mt-5 pt-4 border-t border-gray-100 text-center">
                {ACTIONS.map(({ Icon, label, testid, onClick }) => (
                  <button key={testid} type="button" onClick={onClick} disabled={testid === "action-download" && pdfBusy} className="flex flex-col items-center gap-1.5 text-[11px] text-[#0b2545]/80 hover:text-[var(--yellow-2)] transition disabled:opacity-60" data-testid={testid}>
                    <Icon className={`w-4 h-4 ${testid === "action-download" && pdfBusy ? "animate-bounce" : ""}`} /> {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lower content + sticky enquire */}
      <section className="max-w-[1280px] mx-auto px-4 lg:px-6 py-12">
        <div className="grid lg:grid-cols-[1fr_320px] gap-8 items-start">
          <div className="min-w-0">
            <Tabs defaultValue="itinerary" className="w-full">
              <TabsList className="mb-8 flex h-auto w-full flex-wrap justify-start gap-2 rounded-none border-b border-gray-200 bg-transparent p-0 pb-3">
                {[
                  ["itinerary", "Itinerary"],
                  ["highlights", "Highlights"],
                  ["inclusions", "Inclusions"],
                  ["information", "Tour Info"],
                  ["dates", "Dates & EMI"],
                  ["know", "Need to Know"],
                  ["cancellation", "Cancellation"],
                  ["upgrades", "Upgrades"],
                  ["faq", "FAQ"],
                ].map(([v, l]) => (
                  <TabsTrigger
                    key={v}
                    value={v}
                    data-testid={`tab-${v}`}
                    className="shrink-0 rounded-full border border-gray-200 px-4 py-2 text-[13px] font-medium text-[#0b2545]/70 transition-colors hover:border-[var(--navy)] data-[state=active]:border-[var(--navy)] data-[state=active]:bg-[var(--navy)] data-[state=active]:text-white data-[state=active]:shadow-sm"
                  >
                    {l}
                  </TabsTrigger>
                ))}
              </TabsList>

              <TabsContent value="itinerary" data-testid="tabpanel-itinerary" className="focus-visible:outline-none">
            {/* Itinerary */}
            <div id="itinerary" data-testid="itinerary-section">
              <h2 className="font-serif-display text-2xl text-[var(--navy)] mb-5">Day-wise Itinerary</h2>
              <Accordion type="single" collapsible defaultValue="day-0" className="space-y-3">
                {itinerary.map((d, i) => (
                  <AccordionItem key={i} value={`day-${i}`} className="border border-gray-200 rounded-lg px-4 data-[state=open]:border-[#f59e0b]/40 data-[state=open]:bg-[#fffdf5]">
                    <AccordionTrigger className="hover:no-underline text-left">
                      <span className="flex items-center gap-3">
                        <span className="w-9 h-9 shrink-0 rounded-full bg-[var(--navy)] text-white text-[12px] font-bold flex items-center justify-center">D{d.day}</span>
                        <span className="font-semibold text-[15px] text-[var(--navy)]">{d.title}</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-[14px] text-gray-600 leading-relaxed pl-12">{d.desc}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>

              </TabsContent>

              <TabsContent value="highlights" data-testid="tabpanel-highlights" className="focus-visible:outline-none">
            {/* Highlights */}
            <div data-testid="highlights-section">
              <h2 className="font-serif-display text-2xl text-[var(--navy)] mb-5">Tour Highlights</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {tour.highlights.map((h) => (
                  <div key={h} className="flex items-center gap-2.5 bg-[var(--soft)] rounded-lg px-4 py-3 text-[14px] text-[#0b2545]/85">
                    <CheckCircle2 className="w-4 h-4 text-[var(--yellow-2)] shrink-0" /> {h}
                  </div>
                ))}
              </div>
            </div>

              </TabsContent>

              <TabsContent value="inclusions" data-testid="tabpanel-inclusions" className="space-y-12 focus-visible:outline-none">
            {/* Inclusions */}
            <div data-testid="inclusions-section">
              <h2 className="font-serif-display text-2xl text-[var(--navy)] mb-5">Tour Includes</h2>
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex flex-wrap justify-center gap-8">
                  {inclusions.map((it) => <InclusionIcon key={it.label} {...it} />)}
                </div>
                <div className="mt-6 pt-5 border-t border-gray-100 flex items-center gap-3 text-[14px] text-[#0b2545]/80">
                  <UserRound className="w-8 h-8 text-[var(--navy)]" strokeWidth={1.3} /> Tour includes the services of Royal Tours' experienced Tour Manager throughout your journey.
                </div>
              </div>
            </div>

            {/* Tour details — flights & accommodation */}
            <div data-testid="tour-details-section">
              <h2 className="font-serif-display text-2xl text-[var(--navy)] mb-1">Tour Details</h2>
              <p className="text-[14px] text-gray-500 mb-5">Best facilities with no added cost.</p>
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex items-center gap-2 font-semibold text-[var(--navy)] mb-4">
                  <PlaneTakeoff className="w-5 h-5 text-[var(--yellow-2)]" /> Departure &amp; Arrival (Ex-{depCity})
                </div>
                <div className="grid sm:grid-cols-2 gap-4 text-[14px]">
                  <div className="rounded-lg bg-[var(--soft)] p-4">
                    <div className="text-[12px] text-gray-500 mb-1">Onward</div>
                    <div className="font-semibold text-[var(--navy)]">{depCity} → {tour.route[0]?.state}</div>
                  </div>
                  <div className="rounded-lg bg-[var(--soft)] p-4">
                    <div className="text-[12px] text-gray-500 mb-1">Return</div>
                    <div className="font-semibold text-[var(--navy)]">{tour.route[tour.route.length - 1]?.state} → {depCity}</div>
                  </div>
                </div>
                <p className="mt-4 text-[12px] text-gray-500 leading-relaxed">
                  Note: Flight & hotel details are tentative only. The airline, departure/arrival times, routing and place of accommodation may change.
                </p>
              </div>
            </div>

              </TabsContent>

              <TabsContent value="information" data-testid="tabpanel-information" className="space-y-6 focus-visible:outline-none">
                  <div>
                    <div className="font-semibold text-[var(--navy)] mb-3">Tour Inclusions</div>
                    <ul className="grid sm:grid-cols-2 gap-2.5">
                      {INCLUSIONS_TEXT.map((t) => (
                        <li key={t} className="flex items-start gap-2 text-[13.5px] text-gray-600"><CheckCircle2 className="w-4 h-4 text-green-600 shrink-0 mt-0.5" /> {t}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="font-semibold text-[var(--navy)] mb-3">Tour Exclusions</div>
                    <ul className="grid sm:grid-cols-2 gap-2.5">
                      {EXCLUSIONS_TEXT.map((t) => (
                        <li key={t} className="flex items-start gap-2 text-[13.5px] text-gray-600"><span className="w-4 h-4 rounded-full border border-[#e11d2e]/40 text-[var(--red)] shrink-0 mt-0.5 flex items-center justify-center text-[10px]">×</span> {t}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="font-semibold text-[var(--navy)] mb-3">Advance Preparation</div>
                    <ul className="space-y-2">
                      {PREP_TEXT.map((t) => (
                        <li key={t} className="flex items-start gap-2 text-[13.5px] text-gray-600"><CheckCircle2 className="w-4 h-4 text-[var(--yellow-2)] shrink-0 mt-0.5" /> {t}</li>
                      ))}
                    </ul>
                  </div>
                </TabsContent>

                <TabsContent value="know" data-testid="tabpanel-know" className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div className="rounded-xl border border-gray-200 p-5">
                      <div className="font-semibold text-[var(--navy)] mb-1">Weather</div>
                      <p className="text-[13.5px] text-gray-600">{tour.travelInfo?.weather || <>For detailed weather information, kindly visit <a href="https://www.accuweather.com" target="_blank" rel="noreferrer" className="text-[var(--navy)] underline">accuweather.com</a>.</>}</p>
                    </div>
                    <div className="rounded-xl border border-gray-200 p-5">
                      <div className="font-semibold text-[var(--navy)] mb-1">Transport</div>
                      <p className="text-[13.5px] text-gray-600">Air travel: {flightRoute}. {tour.travelInfo?.coach || "Coach travel by A/C vehicle — type depends on group size."}</p>
                    </div>
                  </div>
                  <div className="rounded-xl border border-gray-200 p-5">
                    <div className="font-semibold text-[var(--navy)] mb-2">Documents Required for Travel</div>
                    <ul className="space-y-1.5">
                      {DOCS.map((d) => (
                        <li key={d} className="flex items-start gap-2 text-[13.5px] text-gray-600"><FileText className="w-4 h-4 text-[var(--yellow-2)] shrink-0 mt-0.5" /> {d}</li>
                      ))}
                    </ul>
                    {tour.travelInfo?.remarks ? <p className="mt-3 text-[12px] text-gray-500">{tour.travelInfo.remarks}</p> : !tour.travelInfo?.documents && <p className="mt-3 text-[12px] text-gray-500">A valid ID (type & number) is mandatory at the time of booking; please carry the same ID on tour. NRIs & foreign nationals require a valid Indian Visa / OCI / PIO card along with passport.</p>}
                  </div>
                </TabsContent>

                <TabsContent value="cancellation" data-testid="tabpanel-cancellation" className="space-y-5">
                  <p className="text-[13.5px] text-gray-600">Tour price: <b className="text-[var(--navy)]">{formatINR(tour.startingPrice)}</b> per person on twin sharing. Cancellation charges rise closer to departure.</p>
                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                    <table className="w-full text-[13.5px]">
                      <thead className="bg-[var(--soft)] text-[#0b2545]/70 text-[12.5px]">
                        <tr><th className="text-left px-5 py-3 font-semibold">Period before departure</th><th className="text-center px-5 py-3 font-semibold">Deduction</th><th className="text-right px-5 py-3 font-semibold">Cancellation charge</th></tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {cancelTiers.map((t) => (
                          <tr key={t.pct}>
                            <td className="px-5 py-3 text-gray-600">{t.window}</td>
                            <td className="text-center px-5 py-3">{t.pct}%</td>
                            <td className="text-right px-5 py-3 font-semibold text-[var(--navy)]">{formatINR(t.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div>
                    <div className="font-semibold text-[var(--navy)] mb-2">Payment Terms</div>
                    <p className="text-[13px] text-gray-600 leading-relaxed">Payment by cash / cheque / demand draft / debit or credit card. For card payments, an additional 0.50% convenience charge applies. Prices are for Indian nationals only; 5% GST is applicable. Prices vary for NRIs / foreign nationals — please contact your travel advisor.</p>
                    <p className="mt-3 text-[13px] text-gray-600">Need help with changes or cancellations? Call us on <a href="tel:+917052163551" className="font-semibold text-[var(--navy)] underline">+91-7052163551</a>.</p>
                  </div>
                </TabsContent>

                <TabsContent value="upgrades" data-testid="tabpanel-upgrades" className="space-y-5">
                  <p className="text-[13.5px] text-gray-600">Want luxury? Add it at minimal cost.</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="rounded-xl border border-gray-200 p-5">
                      <div className="flex items-center gap-2 font-semibold text-[var(--navy)] mb-1"><PlaneTakeoff className="w-4 h-4 text-[var(--yellow-2)]" /> Flight Upgrade</div>
                      <p className="text-[13px] text-gray-600">Upgrade to business or first class. Contact our team on +91-7052163551 for details.</p>
                    </div>
                    <div className="rounded-xl border border-gray-200 p-5">
                      <div className="flex items-center gap-2 font-semibold text-[var(--navy)] mb-1"><Building2 className="w-4 h-4 text-[var(--yellow-2)]" /> Prime Seat(s)</div>
                      <p className="text-[13px] text-gray-600">Reserve preferred seats on your flights and coaches for extra comfort.</p>
                    </div>
                  </div>
                </TabsContent>

              <TabsContent value="dates" data-testid="tabpanel-dates" className="space-y-12 focus-visible:outline-none">
            {/* Departure dates */}
            <div id="dates" data-testid="departure-dates-section">
              <h2 className="font-serif-display text-2xl text-[var(--navy)] mb-1">Choose a date to plan your dream trip</h2>
              <p className="text-[14px] text-gray-500 mb-5">Get professionally planned holidays with a personal touch.</p>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                {tour.dates.map((d) => (
                  <button key={d.date} type="button" onClick={() => setDatesOpen(true)} className="text-left border border-gray-200 rounded-lg p-3.5 hover:border-[var(--yellow-2)] hover:shadow-md transition group" data-testid={`date-tile-${d.date}`}>
                    <div className="flex items-center gap-1.5 text-[13px] font-semibold text-[var(--navy)]"><CalendarDays className="w-4 h-4 text-[var(--yellow-2)]" /> {d.date}</div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-[16px] font-bold text-[var(--navy)]">{d.price != null ? formatINR(d.price) : "Price on request"}</span>
                      {d.tag && <span className="text-[10px] font-semibold text-green-700 bg-green-50 px-1.5 py-0.5 rounded">↓ {d.tag}</span>}
                    </div>
                    {d.note && <div className="text-[11px] text-[var(--red-2)] font-medium mt-1">{d.note}</div>}
                  </button>
                ))}
              </div>
            </div>

            {/* EMI */}
            <div id="emi" data-testid="emi-section">
              <h2 className="font-serif-display text-2xl text-[var(--navy)] mb-5">EMI Options</h2>
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-[14px]">
                  <thead className="bg-[var(--soft)] text-[#0b2545]/70 text-[13px]">
                    <tr>
                      <th className="text-left px-5 py-3 font-semibold">Plan</th>
                      <th className="text-center px-5 py-3 font-semibold">Months</th>
                      <th className="text-center px-5 py-3 font-semibold">Monthly EMI (INR)</th>
                      <th className="text-center px-5 py-3 font-semibold">Interest</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {emiRows.map((r, i) => (
                      <tr key={r.months}>
                        <td className="px-5 py-3 font-medium text-[var(--navy)]">{i === 0 ? "Standard EMI" : ""}</td>
                        <td className="text-center px-5 py-3">{r.months}</td>
                        <td className="text-center px-5 py-3 font-semibold text-[var(--navy)]">{formatINR(r.monthly)}</td>
                        <td className="text-center px-5 py-3 text-green-700">{r.interest}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="p-5 border-t border-gray-100">
                  <div className="font-semibold text-[var(--navy)] mb-3">Submit details to check EMI eligibility</div>
                  <div className="flex flex-wrap items-center gap-3">
                    <Input value={emiForm.name} onChange={(e) => setEmiForm((f) => ({ ...f, name: e.target.value }))} placeholder="Full Name*" className="flex-1 min-w-[180px] py-5" data-testid="emi-name" />
                    <div className="flex items-center border border-gray-300 rounded-md flex-1 min-w-[180px] focus-within:border-[var(--yellow-2)]">
                      <span className="flex items-center gap-1.5 px-3 border-r border-gray-200 text-sm"><img src={resolveAssetPath("/assets/flags/in.png")} width="20" height="14" alt="in" className="rounded-sm" /><ChevronDown className="w-3.5 h-3.5 text-gray-400" /></span>
                      <input type="tel" value={emiForm.mobile} onChange={(e) => setEmiForm((f) => ({ ...f, mobile: e.target.value.replace(/[^0-9]/g, "") }))} placeholder="Mobile No.*" className="flex-1 bg-transparent outline-none px-3 py-2.5 text-sm" data-testid="emi-mobile" />
                    </div>
                    <Button onClick={submitEmi} data-testid="emi-submit" className="bg-[var(--yellow)] hover:bg-[var(--yellow-2)] text-[var(--navy)] hover:text-white font-semibold py-5 px-6 rounded-md">Submit</Button>
                  </div>
                  <ul className="mt-4 text-[12.5px] text-gray-500 space-y-1.5 list-disc pl-5">
                    <li>EMI is inclusive of the processing fee and applicable GST.</li>
                    <li>To avail this EMI facility, traveler must book flight, hotels, and packages for their preferred destination by opting for EMI option as a payment mode.</li>
                    <li>Adhere to the EMI repayment schedule as per the finance partner's terms.</li>
                  </ul>
                </div>
              </div>
            </div>

              </TabsContent>

              <TabsContent value="faq" data-testid="tabpanel-faq" className="focus-visible:outline-none">
            {/* FAQ */}
            <div data-testid="faq-section">
              <h2 className="font-serif-display text-2xl text-[var(--navy)] mb-5">Frequently Asked Questions</h2>
              <Accordion type="single" collapsible className="space-y-3">
                {(tour.faqs || INDIA_FAQS).map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border border-gray-200 rounded-lg px-4">
                    <AccordionTrigger className="text-[15px] font-semibold text-[var(--navy)] hover:no-underline text-left">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-[14px] text-gray-600 leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sticky enquire card */}
          <aside className="lg:sticky lg:top-24 space-y-4" data-testid="enquire-rail">
            <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-[0_20px_45px_-28px_rgba(11,37,69,0.5)]">
              <div className="font-semibold text-[var(--navy)] mb-4">Select details and enquire for tour</div>
              <div className="mb-4">
                <div className="text-[12px] text-gray-500 mb-1.5">Holiday category</div>
                <RadioGroup defaultValue="Standard" className="flex gap-4">
                  {["Standard", "Deluxe"].map((c) => (
                    <label key={c} className="flex items-center gap-2 text-[13px] text-[var(--navy)] cursor-pointer">
                      <RadioGroupItem value={c} className="text-[var(--navy)] border-gray-300" /> {c}
                    </label>
                  ))}
                </RadioGroup>
              </div>
              <div className="mb-4">
                <div className="text-[12px] text-gray-500 mb-1.5">Room type</div>
                <RadioGroup defaultValue="Base" className="flex gap-4">
                  {["Base", "Premium"].map((c) => (
                    <label key={c} className="flex items-center gap-2 text-[13px] text-[var(--navy)] cursor-pointer">
                      <RadioGroupItem value={c} className="text-[var(--navy)] border-gray-300" /> {c}
                    </label>
                  ))}
                </RadioGroup>
              </div>
              <div className="mb-4">
                <div className="text-[12px] text-gray-500 mb-1.5">Departure Date</div>
                <button type="button" onClick={() => setDatesOpen(true)} className="text-[14px] font-semibold text-[var(--navy)] underline">{tour.dates[0]?.date || "Ask about departures"}</button>
              </div>
              <div className="border-t border-dashed border-gray-200 pt-4 flex items-center justify-between text-[13px]">
                <span className="text-gray-500">EMI from</span>
                <span className="font-semibold text-[var(--navy)] underline">{tour.emiPerMonth == null ? "Price on request" : formatINR(tour.emiPerMonth) + "/mo"}</span>
              </div>
              <div className="mt-3 flex items-end justify-between">
                <div>
                  <div className="text-[12px] text-gray-500">Starts From</div>
                  <div className="text-[13px] font-semibold text-[var(--navy)] underline">Detailed tour price</div>
                </div>
                <div className="text-right">
                  <div className="text-[22px] font-bold text-green-700">{formatINR(tour.startingPrice)}</div>
                  <div className="text-[10px] text-gray-400">per person on twin sharing *Excludes taxes</div>
                </div>
              </div>
              <Button onClick={() => setEnquiry(true)} data-testid="enquire-now-detail" className="w-full mt-4 bg-[var(--navy)] hover:bg-[var(--navy-2)] text-white font-semibold py-5 rounded-lg">
                Enquire Now
              </Button>
              <div className="mt-4 flex items-center justify-between text-[13px] pt-3 border-t border-gray-100">
                <a href="tel:+917052163551" className="inline-flex items-center gap-1.5 font-semibold text-[var(--navy)]"><Phone className="w-4 h-4 text-[var(--yellow-2)]" /> +91-7052163551</a>
                <button type="button" className="text-[var(--navy)] underline">Locate Branch office</button>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <Footer />
      <FloatingWidgets />

      <DatesDialog open={datesOpen} onOpenChange={setDatesOpen} tour={tour} onSelect={() => setEnquiry(true)} />
      <EmailItineraryDialog open={emailOpen} onOpenChange={setEmailOpen} pdfOptions={pdfOptions} />
      <EnquiryDrawer open={enquiry} onOpenChange={setEnquiry} tourTitle={`${tour.code}-${tour.title}`} />
    </div>
  );
}


